<template>
  <!--
    vue3-tabs-component registers its tabs in each `<tab>`'s `onBeforeMount` and
    only activates one in the `<tabs>` `onMounted` - neither hook runs during
    SSR. So server-side the real nav renders as an empty <ul> and every panel is
    `v-show`-hidden. This placeholder nav stands in until that happens, using the
    tab names we already have, so the buttons don't pop into existence on hydration.
  -->
  <ul
    v-if="!tabsReady"
    role="presentation"
    aria-hidden="true"
    :class="NAV_CLASS"
  >
    <li
      v-for="tab in organism"
      :key="tabId(tab)"
      :class="[
        NAV_ITEM_CLASS,
        tab.isDisabled ? 'is-disabled' : '',
        tabId(tab) === placeholderActiveId ? NAV_ITEM_ACTIVE_CLASS : '',
      ]"
    >
      <span
        :class="[
          NAV_ITEM_LINK_CLASS,
          tab.isDisabled ? 'is-disabled' : '',
          tabId(tab) === placeholderActiveId ? NAV_ITEM_LINK_ACTIVE_CLASS : '',
        ]"
      >
        <span class="block px-4 py-4">{{ tab.name }}</span>
      </span>
    </li>
  </ul>

  <tabs
    ref="tabbedContentTabs"
    :options="{
      useUrlFragment: false,
      defaultTabHash: targetTabHash ?? defaultTabHash,
    }"
    :wrapper-class="tabsReady ? 'tabs-component' : 'tabs-component is-loading'"
    :nav-class="NAV_CLASS"
    :nav-item-class="NAV_ITEM_CLASS"
    :nav-item-active-class="NAV_ITEM_ACTIVE_CLASS"
    nav-item-disabled-class="is-disabled"
    :nav-item-link-class="NAV_ITEM_LINK_CLASS"
    :nav-item-link-active-class="NAV_ITEM_LINK_ACTIVE_CLASS"
    nav-item-link-disabled-class="is-disabled"
    panels-wrapper-class="px-8 bg-white tabs-component-panels"
    @changed="tabChanged"
  >
    <!-- Rendered inside the panels wrapper, alongside (not inside) the panels,
         so it is visible while every real panel is still hidden. -->
    <MoleculesTabbedContentSkeleton
      v-if="!tabsReady"
      :layout="skeletonLayout"
      :label="loadingLabel"
    />

    <tab
      panel-class="tabs-component-panel"
      prefix='<span class="block px-4 py-4">'
      suffix="</span>"
      v-for="tab in organism"
      :name="tab.name"
      :id="tab.id"
      :is-disabled="tab.isDisabled"
    >
      <ClientOnly>
        <div
          v-if="variant === 'product-detail' && organism.length > 1"
          class="relative max-w-3xl mx-auto"
        >
          <slot :name="tab.id" />
          <button
            class="absolute left-0 inset-y-1/2"
            @click="prevTab(tab.id)"
            title="Previous Tab"
          >
            <ChevronLeftIcon
              class="w-12 h-12 p-2 text-white rounded-full bg-bc-blue"
              aria-hidden="true"
            />
          </button>
          <button
            class="absolute right-0 inset-y-1/2"
            @click="nextTab(tab.id)"
            title="Next Tab"
          >
            <ChevronRightIcon
              class="w-12 h-12 p-2 text-white rounded-full bg-bc-blue"
              aria-hidden="true"
            />
          </button>
        </div>

        <slot v-else :name="tab.id" />
      </ClientOnly>
    </tab>
  </tabs>
</template>

<style>
.tabs-component-tabs {
  @apply flex-col sm:flex-row gap-1 sm:gap-2;
}

/* Keep the library's own (still empty, then still inactive) nav out of the way
   while the placeholder nav above is standing in for it. */
.tabs-component.is-loading > .tabs-component-tabs {
  @apply !hidden;
}
</style>
<script setup>
import {
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/vue/outline/index.js";
import { Tabs, Tab } from "vue3-tabs-component";
import { ref } from "vue";
const tabbedContentTabs = ref(null);
const neighborhoodDetails = useNeighborhoodDetails();

const props = defineProps({
  organism: Object,
  defaultTabHash: String,
  targetTabHash: String,
  variant: String,
  // Optional copy for the loading placeholder; falls back to a generic label.
  loadingLabel: String,
});

// Shared by the real nav and the placeholder nav so the two look identical.
const NAV_CLASS =
  "relative z-0 flex max-w-full px-8 rounded-tl-lg rounded-tr-lg sm:px-0 tabs-component-tabs gap-2";
const NAV_ITEM_CLASS =
  "tabs-component-tab rounded-tl-lg rounded-tr-lg relative flex-1 overflow-hidden drop-shadow-[0_-3px_4px_rgba(0,0,0,0.1)] bg-bc-dark-blue";
const NAV_ITEM_ACTIVE_CLASS = "is-active !bg-white";
const NAV_ITEM_LINK_CLASS =
  "text-xl font-medium text-center text-white font-ubuntu tabs-component-tab-a";
const NAV_ITEM_LINK_ACTIVE_CLASS = "is-active !text-bc-dark-blue";

// False until the tabs library has mounted, activated a tab, and the panel
// content has rendered. Drives both placeholders.
const tabsReady = ref(false);

const skeletonLayout = computed(() =>
  props.variant === "product-detail" ? "media" : "cards",
);

// Mirrors how vue3-tabs-component derives a tab id when none is given.
const tabId = (tab) => tab.id ?? tab.name?.toLowerCase().replace(/ /g, "-");

// Which tab the library will land on once it mounts. Deliberately ignores the
// URL hash and its localStorage cache, both of which it also consults: those
// are client-only, and the placeholder must render the same on server and client.
const placeholderActiveId = computed(() => {
  const ids = Array.isArray(props.organism) ? props.organism.map(tabId) : [];
  const preferred = props.targetTabHash ?? props.defaultTabHash;
  return ids.includes(preferred) ? preferred : ids[0];
});

const tabChanged = (selectedTab) => {
  neighborhoodDetails.currentTab = selectedTab.tab.computedId;
};

const tabIds = computed(() => {
  return props.organism.map((tab) => tab.id);
});

const nextTab = (tabId) => {
  const currentIndex = tabIds.value.indexOf(tabId);
  const targetIndex =
    currentIndex === tabIds.value.length - 1 ? 0 : currentIndex + 1;
  const target = "#" + tabIds.value[targetIndex];

  tabbedContentTabs.value.selectTab(target);
};

const prevTab = (tabId) => {
  const currentIndex = tabIds.value.indexOf(tabId);
  const targetIndex =
    currentIndex === 0 ? tabIds.value.length - 1 : currentIndex - 1;
  const target = "#" + tabIds.value[targetIndex];

  tabbedContentTabs.value.selectTab(target);
};

const onHashChange = () => {
  if (!tabbedContentTabs.value) return;
  tabbedContentTabs.value.selectTab(window.location.hash);
};

onMounted(async () => {
  window.addEventListener("hashchange", onHashChange, false);

  // Child `mounted` hooks run first, so by now the library has selected a tab
  // and `ClientOnly` has flipped; both are queued into the same render flush.
  // Waiting a tick lets that flush paint the real tabs and panel before the
  // placeholders are torn down, so the section never goes blank in between.
  await nextTick();
  tabsReady.value = true;
});

onBeforeUnmount(() => {
  window.removeEventListener("hashchange", onHashChange);
});
</script>
